import type { WorkspaceSummary } from '@/lib/api/generated/model';

import { ACCESS_COPY, VISIBILITY_NAME } from './accessCopy';

/**
 * What a workspace is to this person, wherever it is listed (spec 196 US3): a home
 * card, the where-you-left-off row, a command palette result.
 *
 * Read from the `access` the list already carries, so it costs nothing. Nothing at
 * all while access is not enforced, as the home page keeps its one list then: every
 * workspace is everyone's, and a mark on each would say nothing.
 */
export interface WorkspaceMarks {
  private: boolean;
  /** Yours to manage: an owner, or — before membership was recorded — its creator. */
  owner: boolean;
  /** Shared with the organization and you are not in it. */
  viewOnly: boolean;
}

export function workspaceMarks(
  entry: Pick<WorkspaceSummary, 'access' | 'visibility' | 'created_by'>,
  currentUserId: string | undefined,
): WorkspaceMarks {
  const access = entry.access;
  if (!access?.enforced) {
    return { private: false, owner: false, viewOnly: false };
  }
  const creator =
    access.role == null &&
    currentUserId !== undefined &&
    entry.created_by?.id === currentUserId;
  return {
    private: entry.visibility === 'private',
    owner: access.role === 'owner' || creator,
    // An owner who cannot work is still an owner: one mark, not two that disagree.
    viewOnly:
      access.can_work === false && !(access.role === 'owner' || creator),
  };
}

/** The marks as words, in the order they are shown: what it is, then who you are. */
export function workspaceMarkWords(marks: WorkspaceMarks): string[] {
  const words: string[] = [];
  if (marks.private) words.push(VISIBILITY_NAME.private);
  if (marks.owner) words.push('Owner');
  if (marks.viewOnly) words.push(ACCESS_COPY.viewOnlyChip);
  return words;
}
